import { isPermutation, replay } from "./tray";
import {
  MAX_MOVES,
  type FifteenDefinition,
  type FifteenProgress,
  type FifteenSubmission,
} from "./types";

export const DEFAULT_RULES = {
  en: "Slide the tiles into order, 1 to the last number reading left to right and top to bottom, with the gap in the bottom-right corner. Only a tile next to the gap can move, into the gap.",
  fr: "Faites glisser les carreaux pour les remettre dans l'ordre, de 1 au dernier nombre en lisant de gauche à droite et de haut en bas, la case vide en bas à droite. Seul un carreau voisin de la case vide peut bouger, et il y glisse.",
};

/** The same starting tray as the backend's `empty_definition()`: two slides from home. */
export const STARTER_TILES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 0, 14, 15];

export function emptyDefinition(): FifteenDefinition {
  return {
    puzzleType: "fifteen_puzzle",
    typeVersion: 1,
    size: 4,
    tiles: [...STARTER_TILES],
    rulesText: { ...DEFAULT_RULES },
  };
}

export function emptyProgress(_definition: FifteenDefinition): FifteenProgress {
  return { version: 1, moves: "", undone: "" };
}

/** Keeps only move letters, in the lower case the schema takes. */
export function sanitizeMoves(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return Array.from(raw.toLowerCase())
    .filter((char) => "udlr".includes(char))
    .join("")
    .slice(0, MAX_MOVES);
}

/** Whether the tray can be played at all (the backend refuses anything else on save). */
export function playable(definition: FifteenDefinition): boolean {
  return isPermutation(definition.tiles ?? [], definition.size);
}

/**
 * Accepts any stored blob (older versions, null, garbage) and returns a log this tray can
 * actually play: a log recorded against another definition is cut at the first slide that
 * no longer works, rather than leaving the tray in a position nobody can reach.
 */
export function migrateProgress(definition: FifteenDefinition, raw: unknown): FifteenProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  if (!playable(definition)) return emptyProgress(definition);
  const source = raw as { moves?: unknown; undone?: unknown };
  const outcome = replay(definition.tiles, definition.size, sanitizeMoves(source.moves));
  return {
    version: 1,
    moves: outcome.moves,
    undone: outcome.illegalAt === null ? sanitizeMoves(source.undone) : "",
  };
}

/** The server replays the same string: what the player did, and nothing else. */
export function serializeSubmission(
  _definition: FifteenDefinition,
  progress: FifteenProgress,
): FifteenSubmission {
  return { moves: progress.moves };
}
